import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { Users, UserPlus, Trash2, ShieldCheck, User as UserIcon, Loader2, Gem, Building2, Pencil, X } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Admin-only Users Manager.
 *
 * Fev/2026 — Municípios:
 *   - Novo role "municipio" (dedicado, para logins de câmara municipal).
 *   - Dropdown de município no formulário de criação e num popover de edição
 *     por linha (permite associar admin/staff a um município para restringir
 *     a visibilidade do dashboard).
 */
const UsersManager = ({ currentUserId }) => {
  const [open, setOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [municipalities, setMunicipalities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ email: '', password: '', name: '', role: 'user', municipality_id: '' });
  const [editingUser, setEditingUser] = useState(null); // user obj em edição
  const [editForm, setEditForm] = useState({ role: '', municipality_id: '' });
  const [q, setQ] = useState('');
  const ql = q.trim().toLowerCase();
  const shownUsers = ql
    ? users.filter((u) => `${u.name || ''} ${u.email || ''}`.toLowerCase().includes(ql))
    : users;

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/users`, { withCredentials: true });
      setUsers(data);
    } catch (e) {
      toast.error('Não foi possível carregar utilizadores');
    } finally {
      setLoading(false);
    }
  };

  const fetchMunicipalities = async () => {
    try {
      const { data } = await axios.get(`${API}/municipalities`);
      setMunicipalities(data);
    } catch (e) {
      // não-crítico — dropdowns ficam vazios mas não bloqueiam o resto
      console.warn('Não foi possível carregar municípios', e);
    }
  };

  useEffect(() => {
    if (open) {
      fetchUsers();
      if (municipalities.length === 0) fetchMunicipalities();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const muniName = (mid) => {
    if (!mid) return null;
    const m = municipalities.find((x) => x.municipality_id === mid);
    return m ? `${m.name}${m.district ? ` (${m.district})` : ''}` : mid;
  };

  const createUser = async (e) => {
    e.preventDefault();
    if (!form.email || form.password.length < 8 || !form.name) {
      toast.error('Email, nome e password (mín. 8 caracteres) obrigatórios');
      return;
    }
    if (form.role === 'municipio' && !form.municipality_id) {
      toast.error('Conta município exige selecionar concelho');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.municipality_id) delete payload.municipality_id;
      await axios.post(`${API}/admin/users`, payload, { withCredentials: true });
      toast.success(`Conta criada para ${form.email}`);
      setForm({ email: '', password: '', name: '', role: 'user', municipality_id: '' });
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao criar conta');
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (u) => {
    setEditingUser(u);
    setEditForm({ role: u.role, municipality_id: u.municipality_id || '' });
  };
  const cancelEdit = () => {
    setEditingUser(null);
    setEditForm({ role: '', municipality_id: '' });
  };
  const saveEdit = async () => {
    if (editForm.role === 'municipio' && !editForm.municipality_id) {
      toast.error('Conta municipio exige município');
      return;
    }
    try {
      await axios.patch(
        `${API}/admin/users/${editingUser.user_id}`,
        {
          role: editForm.role,
          municipality_id: editForm.municipality_id || '',
        },
        { withCredentials: true },
      );
      toast.success(`${editingUser.email} atualizado`);
      cancelEdit();
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao atualizar');
    }
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`Eliminar a conta de ${u.email}? Esta ação é irreversível.`)) return;
    try {
      await axios.delete(`${API}/admin/users/${u.user_id}`, { withCredentials: true });
      toast.success(`Utilizador ${u.email} eliminado`);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao eliminar');
    }
  };

  const subInfo = (u) => {
    if (u.role === 'admin' || u.role === 'staff') return { label: 'GRÁTIS (ROLE)', cls: 'bg-slate-200 text-slate-600' };
    if (u.role === 'municipio') return { label: 'MUNICÍPIO', cls: 'bg-indigo-100 text-indigo-700' };
    if (u.subscription_status === 'exempt') return { label: '💎 ISENTO', cls: 'bg-violet-100 text-violet-700' };
    if (u.subscription_status === 'active' && u.subscription_expires_at && new Date(u.subscription_expires_at) > new Date()) {
      return { label: 'SUBSCRITO', cls: 'bg-emerald-100 text-emerald-700' };
    }
    if (u.trial_ends_at && new Date(u.trial_ends_at) > new Date()) return { label: 'TRIAL', cls: 'bg-sky-100 text-sky-700' };
    if (u.trial_ends_at) return { label: 'EXPIRADO', cls: 'bg-red-100 text-red-600' };
    return { label: 'TRIAL', cls: 'bg-sky-100 text-sky-700' };
  };

  const roleBadgeCls = (role) => {
    if (role === 'admin') return 'bg-rose-600 text-white';
    if (role === 'staff') return 'bg-amber-500 text-white';
    if (role === 'municipio') return 'bg-indigo-600 text-white';
    return 'bg-slate-200 text-slate-700';
  };

  const roleAvatarCls = (role) => {
    if (role === 'admin') return 'bg-rose-100 text-rose-700';
    if (role === 'staff') return 'bg-amber-100 text-amber-700';
    if (role === 'municipio') return 'bg-indigo-100 text-indigo-700';
    return 'bg-slate-100 text-slate-600';
  };

  const toggleExempt = async (u) => {
    const next = u.subscription_status === 'exempt' ? 'none' : 'exempt';
    try {
      await axios.post(`${API}/admin/users/${u.user_id}/subscription`, { status: next }, { withCredentials: true });
      toast.success(next === 'exempt' ? `${u.email} agora é isento (grátis) 💎` : `Isenção removida a ${u.email}`);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao alterar subscrição');
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" data-testid="open-users-manager-btn" title="Gestão de Utilizadores">
          <Users className="w-4 h-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col" data-testid="users-manager-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="w-5 h-5 text-slate-700" /> Gestão de Utilizadores
          </DialogTitle>
          <DialogDescription>
            Cria, promove ou associa contas a municípios. O role <span className="inline-block px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700 text-[10px] font-bold ml-1">municipio</span> exige selecionar um concelho.
          </DialogDescription>
        </DialogHeader>

        {/* Create user form */}
        <Card className="border-slate-200">
          <CardContent className="pt-4">
            <form onSubmit={createUser} className="grid grid-cols-1 md:grid-cols-6 gap-2 items-end">
              <div>
                <Label className="text-xs">Nome</Label>
                <Input data-testid="new-user-name" value={form.name}
                       onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Joana" />
              </div>
              <div>
                <Label className="text-xs">Email</Label>
                <Input data-testid="new-user-email" type="email" value={form.email}
                       onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="joana@tectur.pt" />
              </div>
              <div>
                <Label className="text-xs">Password (≥8)</Label>
                <Input data-testid="new-user-password" type="password" value={form.password}
                       onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="••••••••" />
              </div>
              <div>
                <Label className="text-xs">Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="new-user-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="user">Utilizador</SelectItem>
                    <SelectItem value="staff">Staff</SelectItem>
                    <SelectItem value="admin">Administrador</SelectItem>
                    <SelectItem value="municipio">Município</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Município {form.role === 'municipio' && <span className="text-rose-600">*</span>}</Label>
                <Select value={form.municipality_id || 'none'} onValueChange={(v) => setForm({ ...form, municipality_id: v === 'none' ? '' : v })}>
                  <SelectTrigger data-testid="new-user-municipality"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    <SelectItem value="none">(nenhum)</SelectItem>
                    {municipalities.map((m) => (
                      <SelectItem key={m.municipality_id} value={m.municipality_id}>
                        {m.name} {m.district ? `· ${m.district}` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button type="submit" disabled={saving} className="bg-emerald-700 hover:bg-emerald-800" data-testid="submit-create-user">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <UserPlus className="w-4 h-4 mr-2" />}
                Criar
              </Button>
            </form>
          </CardContent>
        </Card>

        <Input
          placeholder="Procurar por nome ou email…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="mt-2"
          data-testid="users-search-input"
        />

        {/* Users list */}
        <div className="flex-1 min-h-0 overflow-y-auto pr-2 mt-2 space-y-1.5" data-testid="users-list-scroll">
          {loading && (
            <div className="text-center py-8 text-sm text-slate-500">
              <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> A carregar...
            </div>
          )}
          {!loading && shownUsers.length === 0 && (
            <div className="text-center py-8 text-sm text-slate-400" data-testid="users-empty">
              {users.length === 0 ? 'Sem utilizadores' : 'Nenhum utilizador corresponde à pesquisa'}
            </div>
          )}
          {shownUsers.map((u) => {
            const isEditing = editingUser && editingUser.user_id === u.user_id;
            return (
              <div
                key={u.user_id}
                data-testid={`user-row-${u.email}`}
                className={`flex items-center gap-3 p-2.5 rounded-md border ${isEditing ? 'border-indigo-400 bg-indigo-50/40' : 'border-slate-200 bg-white hover:bg-slate-50'}`}
              >
                <div className={`w-9 h-9 rounded-full flex items-center justify-center ${roleAvatarCls(u.role)}`}>
                  {u.role === 'admin' ? <ShieldCheck className="w-4 h-4" />
                    : u.role === 'municipio' ? <Building2 className="w-4 h-4" />
                    : <UserIcon className="w-4 h-4" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-slate-800 truncate flex items-center gap-2">
                    {u.name}
                    {u.user_id === currentUserId && <span className="text-xs text-emerald-700 font-normal">(você)</span>}
                  </div>
                  <div className="text-xs text-slate-500 truncate">
                    {u.email} · {u.has_password ? 'Email/Pass' : 'OAuth'} · {new Date(u.created_at).toLocaleDateString('pt-PT')}
                    {u.municipality_id && (
                      <span className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 text-[10px] font-semibold">
                        <Building2 className="w-3 h-3" /> {muniName(u.municipality_id)}
                      </span>
                    )}
                  </div>
                </div>

                {isEditing ? (
                  <div className="flex items-center gap-2">
                    <Select value={editForm.role} onValueChange={(v) => setEditForm({ ...editForm, role: v })}>
                      <SelectTrigger className="w-32" data-testid={`edit-role-${u.email}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="user">Utilizador</SelectItem>
                        <SelectItem value="staff">Staff</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                        <SelectItem value="municipio">Município</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select
                      value={editForm.municipality_id || 'none'}
                      onValueChange={(v) => setEditForm({ ...editForm, municipality_id: v === 'none' ? '' : v })}
                    >
                      <SelectTrigger className="w-52" data-testid={`edit-municipality-${u.email}`}><SelectValue placeholder="Concelho" /></SelectTrigger>
                      <SelectContent className="max-h-72">
                        <SelectItem value="none">(nenhum)</SelectItem>
                        {municipalities.map((m) => (
                          <SelectItem key={m.municipality_id} value={m.municipality_id}>
                            {m.name} {m.district ? `· ${m.district}` : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button size="sm" onClick={saveEdit} className="bg-emerald-700 hover:bg-emerald-800" data-testid={`save-edit-${u.email}`}>
                      Guardar
                    </Button>
                    <Button size="sm" variant="ghost" onClick={cancelEdit} data-testid={`cancel-edit-${u.email}`}>
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <>
                    <span className={`text-[10px] font-bold px-2 py-1 rounded-full ${roleBadgeCls(u.role)}`}>
                      {u.role.toUpperCase()}
                    </span>
                    <span className={`text-[10px] font-bold px-2 py-1 rounded-full ${subInfo(u).cls}`} title="Estado da subscrição">
                      {subInfo(u).label}
                    </span>
                    {u.role === 'user' && (
                      <Button variant="outline" size="sm" onClick={() => toggleExempt(u)}
                        className={u.subscription_status === 'exempt' ? 'border-violet-400 text-violet-700' : ''}
                        title={u.subscription_status === 'exempt' ? 'Remover isenção' : 'Tornar isento'}
                        data-testid={`toggle-exempt-${u.email}`}
                      >
                        <Gem className="w-4 h-4" />
                      </Button>
                    )}
                    <Button variant="outline" size="sm" disabled={u.user_id === currentUserId}
                      onClick={() => startEdit(u)} data-testid={`edit-user-${u.email}`} title="Editar role/município">
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button variant="outline" size="sm" disabled={u.user_id === currentUserId}
                      onClick={() => deleteUser(u)}
                      className="text-rose-600 hover:bg-rose-50 hover:text-rose-700"
                      data-testid={`delete-user-${u.email}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default UsersManager;
